import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslate, useCosts, $$, normalizeNpi } from "../../helpers";
import { DrugCosts as DrugCostsType, PharmacyType, Plan } from "../../@types";
import {
  OutOfNetworkLabel,
  InNetworkLabel,
  AlertMessage,
} from "../../components";
import { AppContext } from "../../app/store";
import { shouldSuppressDrugPricing } from "../../helpers/planHelpers";
import { getNetworkStatusMessageForPlan } from "../../app/contexts/AlertMessaging/helpers";
import { useAlertMessaging } from "../../app/contexts/AlertMessaging/context";
import { useLocation } from "react-router-dom";
import { FormattedAlertMessage } from "../../app/contexts/AlertMessaging/types";
import { Alert } from "@cmsgov/ds-medicare-gov";

export const planComparePlanAlertMessageTestId =
  "alert-message-plan-compare-plan";

export const planCompareAlertMessageTestId = "alert-message-plan-compare";
export const suppressionAlertMessage = "suppression-alert-message-plan-compare";
export const drugCostsByPharmacyContainer = "drug-costs-by-pharmacy-container";

export type TotalDrugCostsProps = {
  generalAlertMessage: JSX.Element | undefined;
  plan: Plan;
  planId: string;
  selectedPlan?: Plan;
  updateNetworkStatusAlertMessage: (
    message: JSX.Element | null,
    planId: string
  ) => void;
  isYearOverYear?: boolean;
};

export const TotalDrugCosts: FunctionComponent<TotalDrugCostsProps> = ({
  generalAlertMessage,
  plan,
  planId,
  selectedPlan,
  updateNetworkStatusAlertMessage,
  isYearOverYear = false,
}) => {
  const t = useTranslate();
  const location = useLocation();
  const {
    state: { pharmacies, pharmacyType },
  } = useContext(AppContext);
  const hasMailOrderPharmacies = [
    PharmacyType.MAIL_AND_RETAIL,
    PharmacyType.MAIL,
  ].includes(pharmacyType);
  const { costs } = useCosts(plan, undefined, pharmacies, isYearOverYear);

  // Alert messaging
  const {
    state: { messages: allAlertMessages },
  } = useAlertMessaging();
  const [networkStatusMessage, setNetworkStatusMessage] = useState<
    FormattedAlertMessage | undefined
  >(undefined);
  useEffect(() => {
    if (costs.length === 0) {
      return;
    }
    const networkStatusMessage = getNetworkStatusMessageForPlan({
      availableMessages: allAlertMessages,
      costs,
      hasMailOrderPharmacies,
      t,
      pageContext: {
        planId,
        planType: plan.plan_type,
        routerState: {
          nextRoute: {
            ...location,
          },
        },
      },
    });
    setNetworkStatusMessage(networkStatusMessage);
  }, [plan, planId, location, costs]);

  useEffect(() => {
    updateNetworkStatusAlertMessage(
      networkStatusMessage ? (
        <AlertMessage
          {...{
            icon: networkStatusMessage.icon,
            body: networkStatusMessage.body,
            action: networkStatusMessage.action,
            condition: networkStatusMessage.key,
            title: networkStatusMessage.title,
            dataTestId: planComparePlanAlertMessageTestId,
            narrowWidth: true,
          }}
        />
      ) : null,
      planId
    );
  }, [networkStatusMessage, planId]);

  const getPharmacyName = (npi: string): string => {
    if (npi === "") {
      return t("plan_search_prefs.mail_order_pharmacy");
    }

    const pharmacy = pharmacies.find(p => p.npi === npi);

    return pharmacy ? pharmacy.name : "N/A";
  };
  const suppressDrugPricing = shouldSuppressDrugPricing(plan.redactions);
  const showMailOrderNotCovered =
    !costs.find(p => p.mail_order === true) && hasMailOrderPharmacies;

  const mailOrderNotCoveredElem = (
    <div className="ds-u-margin-bottom--2" data-testid="mail-order-not-covered">
      <span>{getPharmacyName("")}</span>
      <OutOfNetworkLabel narrow={true} />
      <div className="ds-u-font-size--sm">
        {t("plan_details.pharmacies.costs_vary_for_mail_order")}
      </div>
    </div>
  );

  const pharmacyNetworkLabel = (pharmacy: DrugCostsType) =>
    pharmacy.in_network ? (
      <InNetworkLabel preferred={pharmacy.preferred} narrow={true} />
    ) : (
      <OutOfNetworkLabel narrow={true} />
    );

  return (
    <td
      className={`${
        selectedPlan?.id === plan.id ? " PlanComparePage__selectedCell" : ""
      }`}
    >
      {generalAlertMessage ? (
        generalAlertMessage
      ) : (
        <>
          <span className="ds-text-heading--md ds-u-display--block ds-u-lg-display--none mct-hide-print">
            {t("plan_terms.estimated_yearly_total_cost")}
          </span>
          {suppressDrugPricing ? (
            <Alert
              heading={t("suppressed_plan_drug_pricing.alert.title")}
              variation="warn"
              hideIcon={true}
              data-testid={suppressionAlertMessage}
            >
              {t("suppressed_plan_drug_pricing.alert.content")}
            </Alert>
          ) : (
            <>
              {!costs.length &&
                showMailOrderNotCovered &&
                mailOrderNotCoveredElem}

              {costs.length > 0 && (
                <>
                  <div data-testid={drugCostsByPharmacyContainer}>
                    {costs.map(pharmacy => {
                      if (
                        pharmacy.mail_order &&
                        [PharmacyType.RETAIL, PharmacyType.NONE].includes(
                          pharmacyType
                        )
                      ) {
                        return null;
                      }

                      const planYearlyPremium =
                        plan.calculated_monthly_premium * 12;
                      let estimatedTotalDrugAndPremium =
                        pharmacy.estimated_yearly_total;

                      if (isYearOverYear) {
                        estimatedTotalDrugAndPremium += planYearlyPremium;
                      } else {
                        estimatedTotalDrugAndPremium +=
                          plan.total_remaining_premium;
                      }

                      return (
                        <div
                          key={normalizeNpi(pharmacy.npi)}
                          className="ds-u-margin-bottom--2 e2e-pc-pharmacy"
                        >
                          <span>{getPharmacyName(pharmacy.npi)}</span>
                          <div>{pharmacyNetworkLabel(pharmacy)}</div>
                          <span className="ds-u-font-weight--bold">
                            {suppressDrugPricing ? (
                              <i>{t("plan_terms.drug_pricing_suppressed")}</i>
                            ) : (
                              $$(estimatedTotalDrugAndPremium)
                            )}
                          </span>
                        </div>
                      );
                    })}
                    {showMailOrderNotCovered && mailOrderNotCoveredElem}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </td>
  );
};
